import type { ReactNode } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";

import {
  ComboBox,
  FieldError,
  Input,
  Label,
  ListBox,
  NumberField,
  Select,
  TextArea,
  TextField,
} from "@heroui/react";
import { Header } from "react-aria-components";
import { useController } from "react-hook-form";

/*
 * Fields of the product form. They take `control` instead of reading a FormProvider, and never pass
 * `disabled` to the controller: a disabled controller drops its value from the submitted data, and the
 * external ID is shown disabled in edit mode but still has to be sent.
 */

type Base<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
};

export function TextF<T extends FieldValues>({
  control,
  name,
  label,
  isRequired,
  isDisabled,
  placeholder,
  dir,
  isMono,
}: Base<T> & { dir?: "rtl" | "ltr"; isMono?: boolean }) {
  const { field, fieldState } = useController({ control, name });

  return (
    <TextField
      className="flex flex-col gap-1.5"
      isDisabled={isDisabled}
      isInvalid={fieldState.invalid}
      isRequired={isRequired}
      name={field.name}
      value={(field.value as string | undefined) ?? ""}
      onBlur={field.onBlur}
      onChange={field.onChange}
    >
      <Label>{label}</Label>
      <Input
        ref={field.ref}
        className={isMono ? "font-mono text-sm" : undefined}
        dir={dir}
        placeholder={placeholder}
        variant="secondary"
      />
      <FieldError>{fieldState.error?.message}</FieldError>
    </TextField>
  );
}

export function AreaF<T extends FieldValues>({
  control,
  name,
  label,
  isRequired,
  isDisabled,
  placeholder,
  dir,
  rows = 3,
  description,
  isMono,
}: Base<T> & {
  dir?: "rtl" | "ltr";
  rows?: number;
  description?: ReactNode;
  isMono?: boolean;
}) {
  const { field, fieldState } = useController({ control, name });

  return (
    <TextField
      className="flex flex-col gap-1.5"
      isDisabled={isDisabled}
      isInvalid={fieldState.invalid}
      isRequired={isRequired}
      name={field.name}
      value={(field.value as string | null | undefined) ?? ""}
      onBlur={field.onBlur}
      onChange={field.onChange}
    >
      <Label>{label}</Label>
      <TextArea
        ref={field.ref}
        className={isMono ? "font-mono text-xs" : undefined}
        dir={dir}
        placeholder={placeholder}
        rows={rows}
        variant="secondary"
      />
      {description ? (
        <span className="text-muted text-xs" data-slot="description">
          {description}
        </span>
      ) : null}
      <FieldError>{fieldState.error?.message}</FieldError>
    </TextField>
  );
}

/** A number with an optional "AED" prefix. An empty box is `undefined`, never 0. */
export function NumF<T extends FieldValues>({
  control,
  name,
  label,
  isRequired,
  isDisabled,
  placeholder,
  prefix,
}: Base<T> & { prefix?: string }) {
  const { field, fieldState } = useController({ control, name });
  const value = field.value as number | null | undefined;

  return (
    <NumberField
      className="flex flex-col gap-1.5"
      isDisabled={isDisabled}
      isInvalid={fieldState.invalid}
      isRequired={isRequired}
      minValue={undefined}
      name={field.name}
      value={value == null || Number.isNaN(value) ? Number.NaN : value}
      variant="secondary"
      onBlur={field.onBlur}
      onChange={(next) => field.onChange(Number.isNaN(next) ? undefined : next)}
    >
      <Label>{label}</Label>
      <NumberField.Group>
        <NumberField.Input
          ref={field.ref}
          placeholder={prefix ? `${prefix} ${placeholder ?? ""}`.trim() : placeholder}
        />
      </NumberField.Group>
      <FieldError>{fieldState.error?.message}</FieldError>
    </NumberField>
  );
}

export type Option = { id: string; name: string };

/** A searchable single choice (supplier, brand). */
export function ComboF<T extends FieldValues>({
  control,
  name,
  label,
  isRequired,
  isDisabled,
  placeholder,
  options,
  empty,
}: Base<T> & { options: Option[]; empty: string }) {
  const { field, fieldState } = useController({ control, name });

  return (
    <ComboBox
      className="flex flex-col gap-1.5"
      isDisabled={isDisabled}
      isInvalid={fieldState.invalid}
      isRequired={isRequired}
      selectedKey={(field.value as string | undefined) || null}
      variant="secondary"
      onBlur={field.onBlur}
      onSelectionChange={(key) => field.onChange(key ? String(key) : "")}
    >
      <Label>{label}</Label>
      <ComboBox.InputGroup>
        <Input placeholder={placeholder} variant="secondary" />
        <ComboBox.Trigger />
      </ComboBox.InputGroup>
      <FieldError>{fieldState.error?.message}</FieldError>
      <ComboBox.Popover>
        <ListBox
          renderEmptyState={() => <p className="p-2 text-sm">{empty}</p>}
        >
          {options.map((option) => (
            <ListBox.Item
              key={option.id}
              id={option.id}
              textValue={option.name}
            >
              {option.name}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </ComboBox.Popover>
    </ComboBox>
  );
}

export type Group = { id: string; title: string; options: Option[] };

/** A multiple choice, flat (`options`) or grouped (`groups`, one titled section per group). */
export function MultiF<T extends FieldValues>({
  control,
  name,
  label,
  isRequired,
  isDisabled,
  placeholder,
  options,
  groups,
}: Base<T> & { options?: Option[]; groups?: Group[] }) {
  const { field, fieldState } = useController({ control, name });
  const item = (option: Option) => (
    <ListBox.Item key={option.id} id={option.id} textValue={option.name}>
      {option.name}
      <ListBox.ItemIndicator />
    </ListBox.Item>
  );

  return (
    <Select
      className="flex flex-col gap-1.5"
      isDisabled={isDisabled}
      isInvalid={fieldState.invalid}
      isRequired={isRequired}
      placeholder={placeholder}
      selectionMode="multiple"
      value={(field.value as string[] | undefined) ?? []}
      variant="secondary"
      onBlur={field.onBlur}
      onChange={(keys) => field.onChange((keys ?? []).map(String))}
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <FieldError>{fieldState.error?.message}</FieldError>
      <Select.Popover>
        <ListBox selectionMode="multiple">
          {groups
            ? groups.map((group) => (
                <ListBox.Section key={group.id}>
                  <Header className="text-muted px-2 py-1 text-xs font-medium">
                    {group.title}
                  </Header>
                  {group.options.map(item)}
                </ListBox.Section>
              ))
            : (options ?? []).map(item)}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}
